"use client";

import { LinkTitle } from "./components";
import useSWR from "swr";
import { fetcher } from "@/lib/fetcher";
import { useKindeBrowserClient } from "@kinde-oss/kinde-auth-nextjs";
import { Appointment } from "@/services/appointments/appointments";
import Link from "next/link";
import { formatDate } from "@/lib/date";
import { getAppointmentTitle } from "@/lib/appointment";
import { SquareArrowOutUpRightIcon } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";

interface MaintenanceWidgetProps {
  limit: number;
}

export const MaintenanceWidget = (props: MaintenanceWidgetProps) => {
  const { accessTokenRaw, isLoading: isKindeLoading } = useKindeBrowserClient();

  // Fetch appointments
  const {
    data: appointments,
    error: error,
    isLoading: isLoading,
  } = useSWR(
    accessTokenRaw
      ? [`/v1/appointments?limit=${props.limit}`, accessTokenRaw]
      : null,
    ([url, token]) => fetcher(url, token)
  );

  // Handle loading and error states
  if (isKindeLoading || isLoading)
    return <LoadingState limit={props.limit} />;
  if (error || !accessTokenRaw) return <ErrorState />;

  return (
    <div className="col-span-2 row-span-1 bg-white rounded-3xl p-4 lg:p-6">
      <LinkTitle
        title="Bekijk alle onderhoudsbeurten"
        href="/onderhoudsoverzicht"
      />
      <TableLayout
        appointments={appointments}
        className="hidden lg:inline-table"
      />

      <div className="lg:hidden">
        {appointments.map((appointment: Appointment) => (
          <div key={appointment.id} className="border-t flex justify-between items-center">

            <div>
              <div className="font-bold">
                {getAppointmentTitle(appointment)}
              </div>
              <div className="text-gray-800">
                {appointment.status === "open" &&
                appointment.dueDate !== null &&
                appointment.dueDate !== undefined
                  ? formatDate(new Date(appointment.dueDate))
                  : formatDate(new Date(appointment.scheduleDate!))}
              </div>
            </div>
            <div className="py-4">
              <span
                className={`inline-flex items-center px-3 py-1 rounded-md text-sm font-medium`}
              >
                {appointment.status === "open" && (
                  <Link href={`/afspraak-inplannen/${appointment.id}`}>
                    <span className="inline-flex items-center px-3 py-1 rounded-md text-sm font-medium bg-blue-100 text-[var(--info)]">
                      Inplannen ➔
                    </span>
                  </Link>
                )}
                {appointment.status === "planned" && (
                  <span className="inline-flex items-center px-3 py-1 rounded-md text-sm font-medium bg-orange-100 text-[var(--warning)]">
                    Ingepland
                  </span>
                )}
                {appointment.status === "completed" && (
                  <span className="inline-flex items-center px-3 py-1 rounded-md text-sm font-medium bg-green-100 text-[var(--success)]">
                    Afgerond
                  </span>
                )}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const TableLayout = ({
  appointments,
  className,
}: {
  appointments: Appointment[];
  className?: string;
}) => {
  return (
    <div className={`min-w-full bg-white ${className}`}>
      <div className="grid grid-cols-[12fr_5fr_5fr_2fr] border-t">
        <div className="py-3 text-left text-sm font-semibold text-black flex items-center">
          Omschrijving
        </div>
        <div className="py-3 text-left text-sm font-semibold text-black flex items-center">
          Datum
        </div>
        <div className="py-3 text-left text-sm font-semibold text-black flex items-center">
          Status
        </div>
        <div className="py-3 text-left text-sm font-semibold text-black flex items-center">
          Details
        </div>
      </div>
      <div className="divide-y">
        {appointments.map((appointment: Appointment) => (
          <div key={appointment.id} className="grid grid-cols-[12fr_5fr_5fr_2fr] border-t">
            <div className="py-4 text-gray-800 flex items-center">
              {getAppointmentTitle(appointment)}
            </div>
            <div className="py-4 text-gray-800 flex items-center">
              {appointment.status === "open" &&
                appointment.dueDate !== null &&
                appointment.dueDate !== undefined
                ? formatDate(new Date(appointment.dueDate))
                : formatDate(new Date(appointment.scheduleDate!))}
            </div>
            <div className="py-4 flex items-center">
              <span className={`inline-flex items-center pr-3 py-1 rounded-md text-sm font-medium`}>
                {appointment.status === "open" && (
                  <Link href={`/afspraak-inplannen/${appointment.id}`}>
                    <span className="cursor-pointer inline-flex items-center px-3 py-1 rounded-md text-sm font-medium bg-blue-100 text-[var(--info)]">
                      Inplannen ➔
                    </span>
                  </Link>
                )}
                {appointment.status === "planned" && (
                  <span className="inline-flex items-center px-3 py-1 rounded-md text-sm font-medium bg-orange-100 text-[var(--warning)]">
                    Ingepland
                  </span>
                )}
                {appointment.status === "completed" && (
                  <span className="inline-flex items-center px-3 py-1 rounded-md text-sm font-medium bg-green-100 text-[var(--success)]">
                    Afgerond
                  </span>
                )}
              </span>
            </div>
            <div className="py-4 flex items-center">
              <Link href={`/afspraak-details/${appointment.id}`} className="text-blue-600 hover:underline">
                <SquareArrowOutUpRightIcon className="h-5 w-5 text-gray-500 hover:text-gray-700" />
              </Link>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const LoadingState = ({ limit }: { limit: number }) => {
  return (
    <div className="col-span-2 row-span-1 bg-white rounded-3xl p-4 lg:p-6">
      <div className="mb-4">
        <Skeleton className="h-4 w-48 bg-gray-100" />
      </div>
      <div className="lg:hidden">
        {Array.from({ length: limit }).map((_, index) => (
          <div
            key={index}
            className="border-t flex justify-between items-center py-2"
          >
            <div>
              <div className="font-bold">
                <Skeleton className="h-4 w-32" />
              </div>
              <div className="text-gray-800">
                <Skeleton className="h-4 w-2" />
              </div>
            </div>
            <div className="py-4">
              <Skeleton className="h-8 w-20" />
            </div>
          </div>
        ))}
      </div>
      <div className="min-w-full hidden lg:inline-table">
        <div className="grid grid-cols-[12fr_5fr_5fr_2fr] border-t">
          <div className="py-3 text-left text-sm font-semibold text-black flex items-center">
            <Skeleton className="h-4 w-24 bg-gray-100" />
          </div>
          <div className="py-3 text-left text-sm font-semibold text-black flex items-center">
            <Skeleton className="h-4 w-20 bg-gray-100" />
          </div>
          <div className="py-3 text-left text-sm font-semibold text-black flex items-center">
            <Skeleton className="h-4 w-20 bg-gray-100" />
          </div>
          <div className="py-3 text-left text-sm font-semibold text-black flex items-center">
            <Skeleton className="h-4 w-20 bg-gray-100" />
          </div>
        </div>
        <div>
          {Array.from({ length: limit }).map((_, index) => (
            <div
              key={index}
              className="grid grid-cols-[12fr_5fr_5fr_2fr] border-t"
            >
              <div className="py-4 flex items-center min-h-[56px] md:min-h-[60px] lg:min-h-[68px]">
                <Skeleton className="h-8 w-48 bg-gray-100" />
              </div>
              <div className="py-4 flex items-center min-h-[56px] md:min-h-[60px] lg:min-h-[68px]">
                <Skeleton className="h-8 w-32 bg-gray-100" />
              </div>
              <div className="py-4 flex items-center min-h-[56px] md:min-h-[60px] lg:min-h-[68px]">
                <Skeleton className="h-8 w-32 bg-gray-100" />
              </div>
              <div className="py-4 flex items-center min-h-[56px] md:min-h-[60px] lg:min-h-[68px]">
                <Skeleton className="h-8 w-12 bg-gray-100" />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const ErrorState = () => {
  return (
    <div className="col-span-2 row-span-1 bg-white rounded-3xl p-6">
      Er is een fout opgetreden
    </div>
  );
};
